import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Tag,
  CheckCircle2,
  Info,
  Loader2,
  Star,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { api, getUser, rupiah } from "../lib/api";
import { Header, Footer, Loading } from "../components/Shell";

// Split & auto-select Manual BCA as PRIMARY. Duitku list stays SECONDARY.
// Backend `/payments/methods` returns items with `paymentMethod === "BCA_MANUAL"`
// for manual transfer; Duitku methods have their real Duitku codes (BC, VA, OV, etc.).
const isBcaManual = (m) =>
  String(m?.paymentMethod || "").toUpperCase() === "BCA_MANUAL";

export function Checkout() {
  const { slug } = useParams();
  const nav = useNavigate();
  const user = getUser();
  const [course, setCourse] = useState(null);
  const [methods, setMethods] = useState({ available: false, methods: [], message: "" });
  const [selected, setSelected] = useState("");
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [couponMsg, setCouponMsg] = useState("");
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [bump, setBump] = useState(null);
  const [bumpChecked, setBumpChecked] = useState(false);
  const [showDuitku, setShowDuitku] = useState(false);
  const bcaMethod = useMemo(
    () => (methods.methods || []).find(isBcaManual),
    [methods]
  );
  const duitkuMethods = useMemo(
    () => (methods.methods || []).filter((m) => !isBcaManual(m)),
    [methods]
  );
  useEffect(() => {
    api(`/courses/${slug}`).then((c) => {
      setCourse(c);
      window.mastofaTrack?.("InitiateCheckout", {
        content_ids: [c.slug],
        content_name: c.title,
        value: c.price,
        currency: "IDR",
      });
      api(`/payments/methods?amount=${c.price}`)
        .then((r) => {
          setMethods(r);
          // Auto-select Manual BCA (PRIMARY). Fallback to first available method.
          const bca = (r.methods || []).find(isBcaManual);
          if (bca) setSelected("BCA_MANUAL");
          else if (r.methods?.length) setSelected(r.methods[0].paymentMethod);
        })
        .catch(() => setMethods({ available: false, methods: [], message: "Metode pembayaran belum tersedia." }));
      api(`/checkout/bump-suggestion/${slug}`).then((r) => setBump(r.bump)).catch(() => {});
    });
  }, [slug]);
  const bumpValue = bumpChecked && bump ? bump.bump_price : 0;
  const total = Math.max(0, (course?.price || 0) + bumpValue - discount);
  const applyCoupon = async () => {
    setCouponMsg("");
    setDiscount(0);
    if (!coupon.trim()) return;
    try {
      const r = await api("/coupons/validate", {
        method: "POST",
        data: { code: coupon.trim(), course_slug: slug },
      });
      setDiscount(r.discount);
      setCouponMsg(
        `Kupon aktif: -${new Intl.NumberFormat("id-ID").format(r.discount)}`
      );
    } catch (e) {
      setDiscount(0);
      setCouponMsg(e.response?.data?.detail || "Kode kupon tidak valid.");
    }
  };
  const submit = async () => {
    if (!terms) {
      setError("Anda harus menyetujui Syarat & Ketentuan.");
      return;
    }
    if (total > 0 && methods.available && !selected) {
      setError("Silakan pilih metode pembayaran.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const r = await api("/payments/checkout", {
        method: "POST",
        data: {
          course_slug: slug,
          payment_method: selected,
          coupon_code: coupon,
          bump_slug: bumpChecked && bump ? bump.slug : "",
        },
      });
      if (r.paid) {
        window.mastofaTrack?.("Purchase", {
          value: 0,
          currency: "IDR",
          content_ids: [slug],
          content_name: course?.title,
          order_id: r.order_id,
        });
        nav("/dashboard");
        return;
      }
      if (r.method === "BCA_MANUAL") {
        // Send user to payment status page which will render BCA instructions
        nav(`/payment/${r.merchant_order_id}`, { replace: true });
        return;
      }
      if (r.payment_url) {
        window.mastofaTrack?.("AddPaymentInfo", { value: total, currency: "IDR" });
        window.location.href = r.payment_url;
        return;
      }
      nav(`/payment/${r.merchant_order_id}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Pembayaran belum berhasil. Silakan coba lagi.");
    } finally {
      setBusy(false);
    }
  };
  if (!course) return <Loading label="Memuat checkout..." />;
  return (
    <div>
      <Header />
      <main className="page checkout-page">
        <span className="section-kicker">CHECKOUT</span>
        <h1>
          Selesaikan pembelian<br />
          <em>dan mulai belajar.</em>
        </h1>
        <div className="checkout-grid">
          <div className="checkout-box">
            <div>
              <span>Produk digital</span>
              <h2>{course.title}</h2>
            </div>
            <div className="checkout-form">
              <label>
                Nama
                <input
                  value={user.name || ""}
                  disabled
                  data-testid="checkout-name-input"
                />
              </label>
              <label>
                Email
                <input value={user.email || ""} disabled data-testid="checkout-email-input" />
              </label>
              <label>
                Kode kupon (opsional)
                <div className="coupon-row">
                  <input
                    value={coupon}
                    onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                    placeholder="MISAL: HEMAT10"
                    data-testid="checkout-coupon-input"
                  />
                  <button
                    type="button"
                    className="button button-small"
                    data-testid="checkout-apply-coupon"
                    onClick={applyCoupon}
                  >
                    Terapkan
                  </button>
                </div>
                {couponMsg && (
                  <small
                    className={discount > 0 ? "coupon-ok" : "coupon-err"}
                    data-testid="checkout-coupon-message"
                  >
                    {couponMsg}
                  </small>
                )}
              </label>
            </div>
            {bump && (
              <label
                className={bumpChecked ? "bump-offer active" : "bump-offer"}
                data-testid="bump-offer"
              >
                <input
                  type="checkbox"
                  checked={bumpChecked}
                  onChange={(e) => setBumpChecked(e.target.checked)}
                  data-testid="bump-checkbox"
                />
                <img src={bump.thumbnail} alt="" />
                <div>
                  <span className="bump-badge">🔥 SPECIAL 30% OFF</span>
                  <b>Tambah juga: {bump.title}</b>
                  <div className="bump-price-row">
                    <span className="strike">{rupiah(bump.regular_price)}</span>
                    <b>{rupiah(bump.bump_price)}</b>
                    <span className="bump-save">hemat {rupiah(bump.savings)}</span>
                  </div>
                </div>
              </label>
            )}
            <div className="payment-methods payment-methods-v2" data-testid="payment-methods">
              <b>Metode pembayaran</b>
              {methods.available ? (
                <>
                  {/* PRIMARY — Transfer Manual BCA */}
                  {bcaMethod && (
                    <label
                      className={
                        selected === "BCA_MANUAL"
                          ? "method-primary active"
                          : "method-primary"
                      }
                      data-testid="payment-method-BCA_MANUAL"
                    >
                      <input
                        type="radio"
                        checked={selected === "BCA_MANUAL"}
                        onChange={() => setSelected("BCA_MANUAL")}
                        data-testid="payment-method-BCA_MANUAL-input"
                      />
                      <div className="method-primary-body">
                        <div className="method-primary-head">
                          <span className="method-primary-star" aria-hidden>
                            <Star size={14} fill="currentColor" />
                          </span>
                          <b>Transfer Manual BCA</b>
                          <span className="method-primary-badge">REKOMENDASI</span>
                        </div>
                        <small>
                          Transfer ke rekening BCA + upload bukti. Verifikasi cepat oleh
                          admin.
                        </small>
                      </div>
                    </label>
                  )}

                  {/* SECONDARY — Pembayaran Otomatis via Duitku */}
                  {duitkuMethods.length > 0 && (
                    <div className="method-secondary-wrap" data-testid="duitku-secondary">
                      <button
                        type="button"
                        className="method-secondary-toggle"
                        onClick={() => setShowDuitku((v) => !v)}
                        data-testid="toggle-duitku-methods"
                        aria-expanded={showDuitku}
                      >
                        <span>
                          Pembayaran Otomatis via Duitku{" "}
                          <em>({duitkuMethods.length} metode)</em>
                        </span>
                        {showDuitku ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                      {showDuitku && (
                        <div className="method-list method-list-secondary">
                          {duitkuMethods.map((m) => (
                            <label
                              key={m.paymentMethod}
                              className={
                                selected === m.paymentMethod ? "method active" : "method"
                              }
                              data-testid={`payment-method-${m.paymentMethod}`}
                            >
                              <input
                                type="radio"
                                checked={selected === m.paymentMethod}
                                onChange={() => setSelected(m.paymentMethod)}
                              />
                              <span>
                                <b>{m.paymentName}</b>
                                <small>Biaya {rupiah(m.totalFee || 0)}</small>
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="method-unavailable" data-testid="payment-method-unavailable">
                  <Info size={17} />
                  <p>{methods.message}</p>
                </div>
              )}
            </div>
            <label className="terms">
              <input
                type="checkbox"
                checked={terms}
                onChange={(e) => setTerms(e.target.checked)}
                data-testid="checkout-terms-input"
              />
              Saya telah membaca dan menyetujui{" "}
              <Link to="/terms">Syarat & Ketentuan</Link>,{" "}
              <Link to="/privacy">Kebijakan Privasi</Link>,{" "}
              <Link to="/refund">Refund</Link>, dan{" "}
              <Link to="/cancellation">Cancellation Policy</Link>.
            </label>
            {error && <div className="error" data-testid="checkout-error">{error}</div>}
            <button
              className="button full"
              disabled={busy || !terms}
              onClick={submit}
              data-testid="checkout-pay-button"
            >
              {busy ? <Loader2 className="spin" size={17} /> : <ShieldCheck size={17} />}
              {total === 0 ? "Aktifkan akses" : "Bayar sekarang"} · {rupiah(total)}
            </button>
            <small className="checkout-note" data-testid="checkout-note">
              {selected === "BCA_MANUAL"
                ? "Transfer manual ke rekening BCA MasTofa, lalu upload bukti transfer. Verifikasi admin maksimal 1x24 jam."
                : "Pembayaran otomatis diproses oleh Duitku. Anda akan diarahkan ke halaman resmi Duitku setelah menekan tombol di atas."}
            </small>
          </div>
          <aside className="order-summary" data-testid="order-summary">
            <span>RINGKASAN</span>
            <div>
              <span>Harga kursus</span>
              <b>{rupiah(course.price)}</b>
            </div>
            {bumpChecked && bump && (
              <div className="discount" data-testid="bump-summary-line">
                <span>+ {bump.title.slice(0, 22)}...</span>
                <b>+{rupiah(bump.bump_price)}</b>
              </div>
            )}
            {discount > 0 && (
              <div className="discount">
                <span>Diskon kupon</span>
                <b>-{rupiah(discount)}</b>
              </div>
            )}
            <div className="total">
              <span>Total</span>
              <strong data-testid="order-total">{rupiah(total)}</strong>
            </div>
            <p className="benefits">
              <CheckCircle2 size={15} /> Akses selamanya
              <br />
              <CheckCircle2 size={15} /> Sertifikat setelah tuntas
              <br />
              <CheckCircle2 size={15} /> Update materi gratis
            </p>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function BundleCheckout() {
  const { bundleId } = useParams();
  const nav = useNavigate();
  const user = getUser();
  const [bundle, setBundle] = useState(null);
  const [methods, setMethods] = useState({ available: false, methods: [] });
  const [selected, setSelected] = useState("");
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showDuitku, setShowDuitku] = useState(false);
  const bcaMethod = useMemo(
    () => (methods.methods || []).find(isBcaManual),
    [methods]
  );
  const duitkuMethods = useMemo(
    () => (methods.methods || []).filter((m) => !isBcaManual(m)),
    [methods]
  );
  useEffect(() => {
    api("/bundles").then((list) => {
      const b = list.find((x) => x.id === bundleId);
      if (!b) {
        setError("Bundle tidak ditemukan.");
        return;
      }
      setBundle(b);
      api(`/payments/methods?amount=${b.price}`)
        .then((r) => {
          setMethods(r);
          const bca = (r.methods || []).find(isBcaManual);
          if (bca) setSelected("BCA_MANUAL");
          else if (r.methods?.length) setSelected(r.methods[0].paymentMethod);
        })
        .catch(() => {});
    });
  }, [bundleId]);
  const submit = async () => {
    if (!terms) return setError("Anda harus menyetujui Syarat & Ketentuan.");
    if (bundle.price > 0 && methods.available && !selected)
      return setError("Silakan pilih metode pembayaran.");
    setError("");
    setBusy(true);
    try {
      const r = await api("/payments/checkout-bundle", {
        method: "POST",
        data: { bundle_id: bundleId, payment_method: selected },
      });
      if (r.paid) return nav("/dashboard");
      if (r.payment_url) {
        window.mastofaTrack?.("AddPaymentInfo", { value: bundle.price, currency: "IDR" });
        window.location.href = r.payment_url;
        return;
      }
      nav(`/payment/${r.merchant_order_id}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Pembayaran belum berhasil.");
    } finally {
      setBusy(false);
    }
  };
  if (error && !bundle)
    return (
      <div>
        <Header />
        <main className="page">
          <p>{error}</p>
        </main>
      </div>
    );
  if (!bundle) return <Loading label="Memuat paket..." />;
  return (
    <div>
      <Header />
      <main className="page checkout-page">
        <span className="section-kicker">CHECKOUT BUNDLE</span>
        <h1>
          {bundle.title}
          <br />
          <em>hemat & instan.</em>
        </h1>
        <div className="checkout-grid">
          <div className="checkout-box">
            <div>
              <span>Bundle · {bundle.courses.length} kelas</span>
              <h2>{bundle.title}</h2>
            </div>
            <div className="checkout-form">
              <label>
                Nama
                <input value={user.name} disabled />
              </label>
              <label>
                Email
                <input value={user.email} disabled />
              </label>
            </div>
            <div className="payment-methods payment-methods-v2">
              <b>Metode pembayaran</b>
              {methods.available ? (
                <>
                  {bcaMethod && (
                    <label
                      className={
                        selected === "BCA_MANUAL"
                          ? "method-primary active"
                          : "method-primary"
                      }
                      data-testid="bundle-method-BCA_MANUAL"
                    >
                      <input
                        type="radio"
                        checked={selected === "BCA_MANUAL"}
                        onChange={() => setSelected("BCA_MANUAL")}
                      />
                      <div className="method-primary-body">
                        <div className="method-primary-head">
                          <span className="method-primary-star" aria-hidden>
                            <Star size={14} fill="currentColor" />
                          </span>
                          <b>Transfer Manual BCA</b>
                          <span className="method-primary-badge">REKOMENDASI</span>
                        </div>
                        <small>
                          Transfer ke rekening BCA + upload bukti. Verifikasi cepat.
                        </small>
                      </div>
                    </label>
                  )}
                  {duitkuMethods.length > 0 && (
                    <div className="method-secondary-wrap">
                      <button
                        type="button"
                        className="method-secondary-toggle"
                        onClick={() => setShowDuitku((v) => !v)}
                        data-testid="bundle-toggle-duitku"
                        aria-expanded={showDuitku}
                      >
                        <span>
                          Pembayaran Otomatis via Duitku{" "}
                          <em>({duitkuMethods.length} metode)</em>
                        </span>
                        {showDuitku ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                      {showDuitku && (
                        <div className="method-list method-list-secondary">
                          {duitkuMethods.map((m) => (
                            <label
                              key={m.paymentMethod}
                              className={
                                selected === m.paymentMethod ? "method active" : "method"
                              }
                              data-testid={`bundle-method-${m.paymentMethod}`}
                            >
                              <input
                                type="radio"
                                checked={selected === m.paymentMethod}
                                onChange={() => setSelected(m.paymentMethod)}
                              />
                              <span>
                                <b>{m.paymentName}</b>
                                <small>Biaya {rupiah(m.totalFee || 0)}</small>
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="method-unavailable">
                  <Info size={17} />
                  <p>{methods.message || "Metode pembayaran belum tersedia."}</p>
                </div>
              )}
            </div>
            <label className="terms">
              <input
                type="checkbox"
                checked={terms}
                onChange={(e) => setTerms(e.target.checked)}
                data-testid="bundle-terms-input"
              />
              Saya telah membaca dan menyetujui{" "}
              <Link to="/terms">Syarat & Ketentuan</Link>,{" "}
              <Link to="/privacy">Kebijakan Privasi</Link>,{" "}
              <Link to="/refund">Refund</Link>, dan{" "}
              <Link to="/cancellation">Cancellation Policy</Link>.
            </label>
            {error && <div className="error">{error}</div>}
            <button
              className="button full"
              disabled={busy || !terms}
              onClick={submit}
              data-testid="bundle-pay-button"
            >
              <ShieldCheck size={17} /> Bayar {rupiah(bundle.price)}
            </button>
          </div>
          <aside className="order-summary">
            <span>ISI BUNDLE</span>
            <ul style={{ padding: 0, listStyle: "none", margin: "0 0 20px", display: "grid", gap: 12 }}>
              {bundle.courses.map((c) => (
                <li key={c.slug} style={{ display: "flex", alignItems: "center", gap: 10, color: "#cbd5e1", fontSize: 13 }}>
                  <CheckCircle2 size={14} style={{ color: "#22c55e" }} /> {c.title}
                </li>
              ))}
            </ul>
            <div>
              <span>Harga reguler</span>
              <b style={{ textDecoration: "line-through", color: "#94a3b8" }}>
                {rupiah(bundle.regular_price)}
              </b>
            </div>
            <div>
              <span>Hemat</span>
              <b style={{ color: "#22c55e" }}>
                -{rupiah(bundle.regular_price - bundle.price)}
              </b>
            </div>
            <div className="total">
              <span>Total bayar</span>
              <strong data-testid="bundle-total">{rupiah(bundle.price)}</strong>
            </div>
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}


export function PaymentStatus() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const o = await api(`/payments/order/${orderId}`);
        if (!mounted) return;
        setOrder(o);
        if (o.status === "PAID") {
          if (!window._mastofaPurchaseFired) {
            window._mastofaPurchaseFired = true;
            window.mastofaTrack?.("Purchase", {
              value: o.amount,
              currency: "IDR",
              content_ids: [o.product_slug],
              content_name: o.product_title,
              order_id: o.merchant_order_id,
            });
          }
          setTimeout(() => nav(`/learn/${o.product_slug}`), 2500);
        }
      } catch (e) {
        setErr(e.response?.data?.detail || "Order tidak ditemukan.");
      }
    };
    load();
    const t = setInterval(load, 4000);
    return () => {
      mounted = false;
      clearInterval(t);
    };
  }, [orderId, nav]);
  if (err)
    return (
      <div className="payment-status-page">
        <p>{err}</p>
      </div>
    );
  if (!order) return <Loading label="Memeriksa pembayaran..." />;
  return (
    <div className="payment-status-page" data-testid="payment-status">
      <Header />
      <main className="page">
        <span className="section-kicker">PEMBAYARAN</span>
        <h1>
          Status:
          <br />
          <em>
            {order.status === "PAID"
              ? "Berhasil"
              : order.status === "PENDING"
              ? "Menunggu pembayaran"
              : order.status === "EXPIRED"
              ? "Kedaluwarsa"
              : "Belum berhasil"}
          </em>
        </h1>
        <div className="status-card">
          <p>
            <b>{order.product_title}</b>
          </p>
          <p>Order ID: {order.merchant_order_id}</p>
          <p>Total: {rupiah(order.amount)}</p>
          {order.status === "PAID" && (
            <p>Course sudah tersedia di akun Anda. Mengarahkan ke ruang kelas...</p>
          )}
          {order.payment_method === "BCA_MANUAL" && ["AWAITING_PAYMENT", "AWAITING_VERIFICATION"].includes(order.status) && (
            <BcaManualInstruction order={order} />
          )}
          {order.status === "AWAITING_VERIFICATION" && order.payment_method === "BCA_MANUAL" && (
            <p className="verify-note" data-testid="bca-verify-note">
              ⏳ Bukti transfer sudah kami terima. Admin akan verifikasi maksimal 1x24 jam.
            </p>
          )}
          {order.status === "REJECTED" && (
            <p className="error" data-testid="bca-rejected">
              ❌ Pembayaran ditolak. {order.verification_note && <>Alasan: {order.verification_note}</>}
            </p>
          )}
          {order.status === "PENDING" && (
            <>
              <p>Pembayaran sedang diverifikasi.</p>
              {order.payment_url && (
                <a className="button" href={order.payment_url} data-testid="continue-payment-link">
                  Lanjutkan pembayaran <ArrowRight size={16} />
                </a>
              )}
            </>
          )}
          {order.status === "FAILED" && (
            <button
              className="button"
              onClick={() => nav(`/checkout/${order.product_slug}`)}
              data-testid="retry-payment-button"
            >
              Coba lagi
            </button>
          )}
          {order.status === "EXPIRED" && (
            <button
              className="button"
              onClick={() => nav(`/checkout/${order.product_slug}`)}
              data-testid="new-payment-button"
            >
              Buat pembayaran baru
            </button>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

// -------------------------------------------------------------------------
// BCA Manual Transfer instruction + proof upload
// -------------------------------------------------------------------------
function BcaManualInstruction({ order }) {
  const [copied, setCopied] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState({ sender_name: "", note: "", file_url: "" });
  const uploadRef = useRef(null);
  const copy = (label, value) => {
    navigator.clipboard.writeText(String(value));
    setCopied(label);
    setTimeout(() => setCopied(""), 1500);
  };
  const uploadFile = async (file) => {
    setBusy(true);
    setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Reuse admin upload endpoint (public safe: only saves file, returns URL)
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/proof`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("mastofa_token")}`,
        },
        body: fd,
      });
      if (!res.ok) throw new Error("Upload gagal");
      const data = await res.json();
      setForm((f) => ({ ...f, file_url: data.url }));
      setMsg("Bukti terunggah. Isi nama pengirim lalu klik Kirim.");
    } catch (e) {
      setMsg(e.message || "Upload gagal, coba lagi.");
    } finally {
      setBusy(false);
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.file_url) {
      setMsg("Upload bukti transfer dulu.");
      return;
    }
    if (!form.sender_name.trim()) {
      setMsg("Isi nama pengirim.");
      return;
    }
    setBusy(true);
    try {
      const r = await api("/payments/bca-manual/submit-proof", {
        method: "POST",
        data: { order_id: order.id, ...form },
      });
      setMsg(r.message || "Bukti terkirim. Admin akan verifikasi.");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Gagal mengirim bukti.");
    } finally {
      setBusy(false);
    }
  };
  const inst = {
    bank: "BCA",
    account_number: "0801344291",
    account_holder: "Mustofa",
    final_amount: order.bca_final_amount || order.amount,
    unique_code: order.bca_unique_code || 0,
    expires_at: order.bca_expires_at,
  };
  const expiresIn = inst.expires_at
    ? new Date(inst.expires_at).toLocaleString("id-ID")
    : null;
  return (
    <div className="bca-instruction" data-testid="bca-instruction">
      <h3>Instruksi Transfer Manual</h3>
      <p className="hint">
        Transfer <b>sesuai nominal persis</b> (termasuk kode unik) ke rekening di bawah,
        lalu upload bukti transfer.
      </p>
      <div className="bca-details">
        <div className="bca-row">
          <span>Bank</span>
          <b>{inst.bank}</b>
        </div>
        <div className="bca-row">
          <span>Nomor Rekening</span>
          <b data-testid="bca-account-number">{inst.account_number}</b>
          <button type="button" className="copy-btn" onClick={() => copy("acc", inst.account_number)}>
            {copied === "acc" ? "✓ Disalin" : "Salin"}
          </button>
        </div>
        <div className="bca-row">
          <span>Atas Nama</span>
          <b>{inst.account_holder}</b>
        </div>
        <div className="bca-row bca-amount-row">
          <span>Nominal Transfer</span>
          <b className="bca-amount" data-testid="bca-final-amount">
            Rp {inst.final_amount.toLocaleString("id-ID")}
          </b>
          <button type="button" className="copy-btn" onClick={() => copy("amt", inst.final_amount)}>
            {copied === "amt" ? "✓ Disalin" : "Salin"}
          </button>
        </div>
        <p className="bca-code-hint">
          <span className="code-badge">Kode unik: <b>{String(inst.unique_code).padStart(3, "0")}</b></span>
          &nbsp; sudah termasuk di nominal transfer di atas.
        </p>
        {expiresIn && (
          <p className="bca-expires">
            ⏰ Batas transfer & upload bukti: <b>{expiresIn}</b>
          </p>
        )}
      </div>
      {order.status === "AWAITING_PAYMENT" && (
        <form className="bca-proof-form" onSubmit={submit} data-testid="bca-proof-form">
          <h4>Upload bukti transfer</h4>
          <input
            ref={uploadRef}
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
            data-testid="bca-proof-file"
          />
          {form.file_url && (
            <small className="hint">
              ✓ Bukti terunggah. <a href={form.file_url} target="_blank" rel="noreferrer">Lihat</a>
            </small>
          )}
          <label>
            Nama pengirim (sesuai rekening pengirim)
            <input
              required
              placeholder="Contoh: Budi Santoso"
              value={form.sender_name}
              onChange={(e) => setForm({ ...form, sender_name: e.target.value })}
              data-testid="bca-sender-name"
            />
          </label>
          <label>
            Catatan (opsional)
            <textarea
              rows="2"
              placeholder="Contoh: Transfer dari BCA jam 14:30"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              data-testid="bca-note"
            />
          </label>
          <button
            className="button full"
            type="submit"
            disabled={busy || !form.file_url}
            data-testid="bca-submit-proof"
          >
            {busy ? "Mengirim..." : "Kirim bukti untuk verifikasi"} <ArrowRight size={15} />
          </button>
          {msg && <div className="error" data-testid="bca-msg">{msg}</div>}
        </form>
      )}
    </div>
  );
}
